import { inventoryValue } from "./appearance";
import React, { useState } from "react";
import {
  Banknote,
  ShoppingBag,
  Package,
  ChartNoAxesCombined,
  Globe,
  ArrowUpRight,
  Plus,
  Download,
  AlertTriangle,
} from "lucide-react";
import { PageHead, Button, Status, Empty, download } from "./ui";
import { money, paidStatuses, date, csvEncode } from "./domain";
export function Dashboard({ data, user, run }) {
  const [period, setPeriod] = useState("month");
  const now = new Date();
  const since =
    period === "all"
      ? new Date(0)
      : period === "month"
        ? new Date(now.getFullYear(), now.getMonth(), 1)
        : new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
  const orders = data.orders.filter((o) => new Date(o.created_at) >= since);
  const paid = data.orders.filter(
    (o) =>
      paidStatuses.includes(o.status) &&
      new Date(o.paid_at || o.created_at) >= since,
  );
  const revenue = paid.reduce((s, o) => s + Number(o.total), 0),
    units = paid.reduce(
      (s, o) => s + o.items.reduce((t, i) => t + i.quantity, 0),
      0,
    );
  const low = data.variants.filter((v) => v.active && v.stock <= v.low_stock);
  const daily = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - 6 + i,
    );
    const amount = data.orders
      .filter(
        (o) =>
          paidStatuses.includes(o.status) &&
          new Date(o.paid_at || o.created_at).toLocaleDateString("es-PE", {
            timeZone: "America/Lima",
          }) === day.toLocaleDateString("es-PE", { timeZone: "America/Lima" }),
      )
      .reduce((s, o) => s + Number(o.total), 0);
    return {
      label: day.toLocaleDateString("es-PE", { weekday: "short" }),
      amount,
    };
  });
  const max = Math.max(...daily.map((d) => d.amount), 1);
  const cat = data.categories
    .map((c) => ({
      name: c.name,
      total: paid.reduce(
        (s, o) =>
          s +
          o.items
            .filter((i) => i.category_id === c.id)
            .reduce((s, i) => s + Number(i.total), 0),
        0,
      ),
    }))
    .sort((a, b) => b.total - a.total);
  const catMax = Math.max(...cat.map((c) => c.total), 1);
  const top = new Map();
  for (const o of paid)
    for (const i of o.items) {
      const prev = top.get(i.sku) || {
        name: i.name,
        color: i.color,
        sku: i.sku,
        quantity: 0,
        total: 0,
      };
      prev.quantity += i.quantity;
      prev.total += Number(i.total);
      top.set(i.sku, prev);
    }
  const tops = [...top.values()].sort((a, b) => b.quantity - a.quantity);
  function report() {
    download(
      csvEncode([
        [
          "Pedido",
          "Fecha venta",
          "Producto",
          "Código",
          "Color",
          "Cantidad",
          "Importe producto",
          "Estado",
        ],
        ...paid.flatMap((o) =>
          o.items.map((i) => [
            o.number,
            date(o.paid_at || o.created_at),
            i.name,
            i.sku,
            i.color,
            i.quantity,
            i.total,
            o.status,
          ]),
        ),
      ]),
      "ventas-por-color-dilo.csv",
      "text/csv;charset=utf-8",
    );
  }
  return (
    <>
      <PageHead
        eyebrow="ASÍ VA TU TIENDA"
        title={`Hola, ${data.members.find((m) => m.user_id === user.id)?.name?.split(" ")[0] || "Dilo"} 👋`}
        description="Cada pedido es una nueva historia. Aquí está el resumen de tu negocio."
        actions={
          <>
            <select
              className="control"
              style={{ width: "auto" }}
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="month">Este mes</option>
              <option value="week">Últimos 7 días</option>
              <option value="all">Todo el historial</option>
            </select>
            <Button kind="outline" onClick={report}>
              <Download size={16} /> Reporte
            </Button>
          </>
        }
      />
      <div className="visit-banner">
        <div className="flex">
          <Globe size={27} />
          <div>
            <p>Visitas a tu tienda</p>
            <strong>{data.visits}</strong>
          </div>
        </div>
        <div>
          <p>Tu vitrina está lista para compartir</p>
          <span style={{ fontSize: ".85rem" }}>
            Dilo Shop <ArrowUpRight size={14} />
          </span>
        </div>
        <Button
          onClick={() =>
            run(
              () => navigator.clipboard.writeText(`${location.origin}/`),
              "Enlace de tienda copiado",
            ).catch(() => {})
          }
        >
          Compartir tienda <ArrowUpRight size={16} />
        </Button>
      </div>
      <div className="card inventory-value" style={{marginBottom: 22}}>
        <div><span className="eyebrow">VALOR DEL INVENTARIO</span><h2>{money(inventoryValue(data.products, data.variants))}</h2></div>
        <p>{data.variants.reduce((n,v) => n + v.stock, 0)} unidades disponibles × su precio de venta actual.<br/><small>Valor potencial del stock, antes de costos y gastos. No es ganancia realizada.</small></p>
      </div>
      <div className="stat-grid">
        {[
          {
            name: "Ingresos confirmados",
            value: money(revenue),
            note: "Pagados, en ruta y entregados",
            Icon: Banknote,
          },
          {
            name: "Pedidos del período",
            value: orders.length,
            note: "Todos los estados",
            Icon: ShoppingBag,
          },
          {
            name: "Unidades vendidas",
            value: units,
            note: "Con pago confirmado",
            Icon: Package,
          },
          {
            name: "Ticket promedio",
            value: money(paid.length ? revenue / paid.length : 0),
            note: `${paid.length} ventas confirmadas`,
            Icon: ChartNoAxesCombined,
          },
        ].map(({ name, value, note, Icon }) => (
          <div className="stat-card" key={name}>
            <div className="stat-top">
              {name}
              <span className="stat-icon">
                <Icon size={18} />
              </span>
            </div>
            <strong>{value}</strong>
            <small>{note}</small>
          </div>
        ))}
      </div>
      <div className="order-status-summary">
        {["Pendiente", "Aceptado", "Pagado", "En ruta", "Entregado"].map(
          (s) => (
            <button
              key={s}
              onClick={() =>
                (location.hash = `#admin/orders?estado=${encodeURIComponent(s)}`)
              }
            >
              <Status value={s} />
              <strong>{orders.filter((o) => o.status === s).length}</strong>
              <small>
                {money(
                  orders
                    .filter((o) => o.status === s)
                    .reduce((n, o) => n + Number(o.total), 0),
                )}
              </small>
            </button>
          ),
        )}
      </div>
      <div className="dashboard-grid">
        <section className="card">
          <div className="card-head">
            <div>
              <h3>Ventas de la semana</h3>
              <p>Ingresos confirmados durante los últimos 7 días</p>
            </div>
            <span className="status s-pagado">
              {money(daily.reduce((s, d) => s + d.amount, 0))}
            </span>
          </div>
          <div className="chart">
            {daily.map((d, i) => (
              <div
                className="chart-bar"
                key={i}
                title={`${d.label}: ${money(d.amount)}`}
              >
                <div
                  style={{ height: `${Math.max(1, (d.amount / max) * 100)}%` }}
                />
                <small>{d.label}</small>
              </div>
            ))}
          </div>
          <div className="chart-values">
            <span>
              <i /> Ventas confirmadas
            </span>
            {!daily.some((d) => d.amount) && (
              <span>Tu primera venta dará vida a este gráfico.</span>
            )}
          </div>
        </section>
        <section className="card">
          <div className="card-head">
            <div>
              <h3>Ventas por categoría</h3>
              <p>Importe de productos, sin envío</p>
            </div>
          </div>
          <div className="category-stats">
            {cat.slice(0, 5).map((c) => (
              <div key={c.name}>
                <p>
                  {c.name}
                  <strong>{money(c.total)}</strong>
                </p>
                <div className="progress-track">
                  <span style={{ width: `${(c.total / catMax) * 100}%` }} />
                </div>
              </div>
            ))}
            {!cat.length && <p>Agrega categorías para ver el desglose.</p>}
          </div>
        </section>
      </div>
      <div className="dashboard-grid">
        <section className="card">
          <div className="card-head">
            <h3>Últimos pedidos</h3>
            <a className="text-button" href="#admin/orders">
              Ver todos <ArrowUpRight size={14} />
            </a>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Pedido</th>
                  <th>Cliente</th>
                  <th>Estado</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.orders.slice(0, 5).map((o) => (
                  <tr key={o.id}>
                    <td>
                      <a className="row-link" href={`#admin/order/${o.id}`}>
                        {o.number}
                      </a>
                    </td>
                    <td>{o.customer_name}</td>
                    <td>
                      <Status value={o.status} />
                    </td>
                    <td>{money(o.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!data.orders.length && (
            <Empty
              title="Tu próxima venta empieza aquí"
              action={
                <a className="btn soft" href="#admin/manual">
                  <Plus size={16} /> Crear pedido manual
                </a>
              }
            />
          )}
        </section>
        <section className="card">
          <div className="card-head">
            <h3>Atención al inventario</h3>
            <AlertTriangle size={18} color="#ce9b47" />
          </div>
          <p style={{ fontSize: ".82rem" }}>
            {low.length} colores con stock bajo o agotado
          </p>
          {low.slice(0, 4).map((v) => (
            <div className="detail-item" key={v.id}>
              <div>
                <strong>
                  {data.products.find((p) => p.id === v.product_id)?.name}
                </strong>
                <small>
                  {v.color} · {v.sku}
                </small>
              </div>
              <span className="status s-pendiente">{v.stock}</span>
            </div>
          ))}
          <a className="text-button" href="#admin/inventory">
            Revisar inventario <ArrowUpRight size={14} />
          </a>
        </section>
      </div>
      <section className="card">
        <div className="card-head">
          <h3>Productos y colores más vendidos</h3>
          <Button kind="outline" onClick={report}>
            <Download size={15} /> Exportar ventas por color
          </Button>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Producto</th>
                <th>Código</th>
                <th>Color</th>
                <th>Unidades</th>
                <th>Ingresos</th>
              </tr>
            </thead>
            <tbody>
              {tops.slice(0, 10).map((p) => (
                <tr key={p.sku}>
                  <td>{p.name}</td>
                  <td>{p.sku}</td>
                  <td>{p.color}</td>
                  <td>{p.quantity}</td>
                  <td>{money(p.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!tops.length && (
            <p className="notice">
              El desglose aparecerá cuando confirmes el primer pago.
            </p>
          )}
        </div>
      </section>
    </>
  );
}
